import { grouped, groupedWeight } from "@/app/lib/number";
import type { MovementPointModel, MovementStatsModel } from "./movements.model";

/**
 * WHAT A MOVEMENT CHART PLOTS, AND HOW ITS AXIS IS DECIDED. Pure, no React, no formatting —
 * which is what lets every decision here be tested against fixed numbers instead of a rendering.
 */

/**
 * THE FIVE THINGS WORTH PLOTTING ABOUT A MOVEMENT, AND ONE OF THEM MATTERS MORE.
 *
 * ═══ 🚨 `e1rm` IS THE ONE THAT SHOWS PROGRESS. THE REST ARE QUANTITIES OF WORK ═══
 *
 * A volume says how much you did. A top weight says the most you had on the bar — and it cannot
 * tell 100 kg × 5 from 110 kg × 1, which is the same athlete on a good week, so it calls the
 * second one an improvement. A rep count says even less on its own.
 *
 * The estimated max folds the reps and the weight into ONE number, which is what makes two
 * different sessions comparable at all. It leads the metric list and it is what the screen opens
 * on for any movement that has ever been loaded and measured.
 *
 * ⚠️ IT IS AN ESTIMATE AND THE SCREEN SAYS SO EVERY TIME IT PRINTS ONE.
 *
 * ═══ 🚨 WHY `topLoad` IS NOT ALWAYS OFFERED ═══
 *
 * A back squat has a weight; a pull-up does not. Drawing "top weight" for a pull-up is a flat
 * line along the bottom of an axis, under a heading that implies the athlete has made no
 * progress in a year — which is not a gap in the data, it is a chart making a claim about
 * somebody. So the metric list is derived from the movement (see {@link metricsFor}) rather
 * than being a fixed row of chips with two of them permanently dead.
 *
 * ═══ WHY `volume` IS THE DEFAULT FOR A LOADED MOVEMENT AND `reps` FOR THE REST ═══
 *
 * Volume — Σ(reps × kilos) — is the number a training block is actually judged on, and it is
 * the one an athlete recognises from every other app. For a movement with no load it is
 * permanently zero, so the honest default there is the count.
 */
export const MOVEMENT_METRICS = ["e1rm", "volume", "topLoad", "reps", "sets"] as const;
export type MovementMetric = (typeof MOVEMENT_METRICS)[number];

/**
 * Which metrics this movement can honestly draw. Order is the order the chips appear in.
 *
 * ═══ 🚨 TWO FLAGS, BECAUSE THEY ANSWER TWO DIFFERENT QUESTIONS ═══
 *
 * `loaded` — has any set carried weight, a derived metcon line included? That is what makes a
 * VOLUME meaningful: metcon work is real work and belongs in a training volume.
 *
 * `measuredLoad` — has a PERSON ever entered a weight for it? That is what makes a TOP WEIGHT
 * meaningful, and it is a stricter question. A derived line carries the load the workout
 * prescribed, so a movement that only ever appears in metcons has a "top weight" that is the
 * same number every week — a flat line reading as an athlete who has not progressed in a year,
 * when the truth is that nobody ever measured it.
 */
export function metricsFor(
  stats: Pick<MovementStatsModel, "loaded" | "measuredLoad">,
): MovementMetric[] {
  const metrics: MovementMetric[] = [];
  /* 🚨 FIRST, AND FIRST IS THE POINT. Everything else on this screen is a quantity of work; the
     estimated max is the only one that answers "am I getting stronger" with a single comparable
     number, so it leads the row and is what the screen opens on. */
  if (stats.measuredLoad) metrics.push("e1rm");
  if (stats.loaded) metrics.push("volume");
  if (stats.measuredLoad) metrics.push("topLoad");
  metrics.push("reps", "sets");
  return metrics;
}

/** What a screen opens on. The first metric it is allowed to offer, which is never empty. */
export function defaultMetric(
  stats: Pick<MovementStatsModel, "loaded" | "measuredLoad">,
): MovementMetric {
  return metricsFor(stats)[0] ?? "reps";
}

/**
 * THE LATEST ESTIMATE AND THE BEST ONE IN THE WINDOW.
 *
 * 🔑 BOTH, BECAUSE THEY ANSWER DIFFERENT QUESTIONS. The latest is where the athlete IS; the best
 * is what they have done. Showing only the best turns a screen into a trophy cabinet that never
 * moves after a good day; showing only the latest hides that a bad session is a bad session
 * rather than a decline.
 *
 * ⚠️ "LATEST" IS THE LAST BUCKET THAT PRODUCED ONE, not the last bucket. A week of metcons
 * yields no estimate, and treating that as a drop to nothing would draw a cliff on the one
 * chart an athlete reads for reassurance.
 */
export function estimatedMaxSummary(points: readonly MovementPointModel[]): {
  latest: number | null;
  best: number | null;
} {
  const values = points
    .map((point) => point.estimatedMaxKg)
    .filter((value): value is number => value !== null);

  return {
    latest: values.length === 0 ? null : values[values.length - 1]!,
    best: values.length === 0 ? null : Math.max(...values),
  };
}

/**
 * ONE POINT'S VALUE FOR ONE METRIC.
 *
 * ⚠️ `null` IS A REAL ANSWER AND IS NOT ZERO. A week in which the movement was trained but
 * nothing carried a weight has no top load — plotting that as zero would draw a collapse to an
 * empty bar that never happened. The chart breaks its line there, exactly as the heart-rate
 * trace breaks across a dropout, and for the same reason.
 */
export function valueOf(point: MovementPointModel, metric: MovementMetric): number | null {
  switch (metric) {
    case "e1rm":
      return point.estimatedMaxKg;
    case "volume":
      return point.volumeKg;
    case "topLoad":
      return point.topLoadKg;
    case "reps":
      return point.reps;
    case "sets":
      return point.sets;
  }
}

/**
 * IS THIS METRIC A WEIGHT — which is to say, does it have to be converted before it is printed?
 *
 * 🔑 EVERYTHING ON THE WIRE IS KILOS, and the athlete may be reading pounds. This is the one
 * question a screen has to ask before drawing a number, and getting it wrong in either
 * direction produces a plausible figure that is off by 2.2 — the worst kind of wrong, because
 * nothing looks broken.
 */
export function isWeight(metric: MovementMetric): boolean {
  return metric === "volume" || metric === "topLoad" || metric === "e1rm";
}

/**
 * THE VERTICAL WINDOW FOR A SERIES.
 *
 * ═══ ⚠️ IT STARTS AT ZERO FOR A VOLUME AND HUGS THE DATA FOR A LOAD, AND THAT IS DELIBERATE ═══
 *
 * A VOLUME is a quantity of work: half as much is half as tall, and an axis that started at
 * 4,000 would draw a 5% week-to-week wobble as a mountain range. Zero-based is the only honest
 * baseline for something you can do less of.
 *
 * A TOP LOAD is not. Nobody's squat goes to zero, so a zero-based axis squeezes a year of
 * genuine progress — 100 kg to 130 — into the top quarter of the box and the line looks flat.
 * There the window hugs the data with a margin, which is the standard reading of a strength
 * chart and is what every athlete already expects from one.
 *
 * A FLAT SERIES gets a window anyway. Without the guard, `min === max` divides by zero and the
 * whole chart renders as `NaN` — one athlete who has done exactly one session of a movement is
 * enough to hit it.
 */
export function bounds(values: readonly number[], zeroBased: boolean): { min: number; max: number } {
  const real = values.filter((value) => Number.isFinite(value));
  if (real.length === 0) return { min: 0, max: 1 };

  const low = Math.min(...real);
  const high = Math.max(...real);

  if (zeroBased) {
    /* A ceiling of zero means every point is zero, which is a real series — a movement whose
       every set was bodyweight, asked for its volume. It needs a box to be flat inside. */
    return { min: 0, max: high > 0 ? high : 1 };
  }

  if (high === low) {
    /* One point, or a year of identical sets. A tenth either side gives the marker somewhere to
       sit; without it the line is drawn on the boundary of a zero-height box. */
    const pad = Math.max(1, Math.abs(high) * 0.1);
    return { min: low - pad, max: high + pad };
  }

  const pad = (high - low) * 0.15;
  return { min: low - pad, max: high + pad };
}

/**
 * IS A SERIES WORTH DRAWING AT ALL?
 *
 * ⚠️ ONE POINT IS NOT A CHART. A line needs two points and a trend needs several; a single
 * marker floating in an empty box tells an athlete nothing they did not already know from the
 * number above it, and it looks like a chart that failed to load. The screen shows the totals
 * and says the history is still short.
 */
export function hasSeries(points: readonly MovementPointModel[], metric: MovementMetric): boolean {
  return points.filter((point) => valueOf(point, metric) !== null).length >= 2;
}

/**
 * HOW MUCH OF A NUMBER WAS ACTUALLY MEASURED, as a fraction between 0 and 1.
 *
 * 🚨 THIS IS THE HONESTY FIGURE FOR THE MOVEMENT SCREENS, and it is the same job
 * `coveredSeconds` does for a heart-rate trace. A `derived` set is a metcon's prescription
 * multiplied by the rounds banked — real work, in a real volume total, that nobody counted. An
 * athlete looking at "272,046 kg" deserves to know whether that came from sets they typed or
 * from arithmetic.
 *
 * Returns 1 for a movement with no sets at all, because there is nothing unmeasured about
 * nothing — and a screen showing "0% measured" over an empty movement would be alarming about
 * a non-event.
 */
export function measuredShare(totals: { sets: number; measuredSets: number }): number {
  if (totals.sets <= 0) return 1;
  return Math.min(1, totals.measuredSets / totals.sets);
}

/**
 * ═══ 🚨 THE MAX, PRINTED — ONE FUNCTION FOR THE LIST ROW AND THE HEADLINE (BUD-375) ═══
 *
 * The two screens must print the same figure the same way, and the honest way to guarantee it
 * is one function rather than two matching comments. The precision follows the SOURCE:
 *
 *   · `measured` / `reported` keep the half-kilo — "137.5" is a number somebody put on a bar,
 *     and the half is the smallest pair of plates a metric gym owns.
 *   · `estimated` / `derived` / `assumed` are whole units — "159.3" claims a tenth of a kilo of
 *     precision that a table of seventeen coefficients does not have, and an athlete reads a
 *     decimal as a measurement.
 *
 * Returns the number only; the unit is the screen's to place.
 */
export function printedMax(
  max: { kilos: number; source: string },
  fromKilos: (kilos: number) => number,
): string {
  const precise = max.source === "measured" || max.source === "reported";
  return precise ? groupedWeight(fromKilos(max.kilos)) : grouped(fromKilos(max.kilos));
}
